import { useState, useEffect, useCallback } from "react";
import { Loader2, Inbox, AlertTriangle, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { downloadCSV } from "@/lib/csv";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export function useFetch(path, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const refetch = useCallback(() => {
    if (!path) { setState({ data: null, loading: false, error: null }); return; }
    setState((s) => ({ ...s, loading: true, error: null }));
    api
      .get(path)
      .then((r) => setState({ data: r.data, loading: false, error: null }))
      .catch((e) => setState({ data: null, loading: false, error: fmtErr(e) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refetch(); }, deps);
  return { ...state, refetch, setData: (d) => setState((s) => ({ ...s, data: d })) };
}

export const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtDate = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight font-outfit" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-muted-foreground" data-testid="loading-state">
      <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function EmptyState({ label = "No records found", hint }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
      <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
        <Inbox className="h-6 w-6 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium">{label}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

export function ErrorState({ error, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="error-state">
      <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mb-3">
        <AlertTriangle className="h-6 w-6 text-destructive" />
      </div>
      <p className="text-sm font-medium text-destructive">Failed to load</p>
      <p className="text-xs text-muted-foreground mt-1 max-w-md">{String(error)}</p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry} data-testid="retry-btn">
          <RefreshCw className="h-3.5 w-3.5 mr-2" /> Retry
        </Button>
      )}
    </div>
  );
}

const CHIP = {
  green: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  red: "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20",
  blue: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20",
  gray: "bg-muted text-muted-foreground border-border",
};
const STATUS_TONE = {
  APPROVED: "green", DELIVERED: "green", PAID: "green", QUALIFIED: "green", ACTIVE: "green", OPEN: "amber",
  PENDING: "amber", CONFIRMED: "blue", PREPARING: "blue", READY_FOR_PICKUP: "blue", ASSIGNED: "blue",
  PICKED_UP: "blue", OUT_FOR_DELIVERY: "blue", REJECTED: "red", CANCELLED: "red", REFUNDED: "red",
  SUSPENDED: "red", FAILED: "red", CLOSED: "gray",
};
export function StatusChip({ status, tone }) {
  const t = tone || STATUS_TONE[status] || "gray";
  return (
    <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border tnum", CHIP[t])} data-testid={`status-${status}`}>
      {String(status ?? "—").replace(/_/g, " ")}
    </span>
  );
}

export function Paginator({ page, total, limit, onPage }) {
  const pages = Math.max(1, Math.ceil((total || 0) / (limit || 20)));
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 text-sm" data-testid="paginator">
      <span className="text-muted-foreground">Page {page} of {pages} · {total} total</span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="prev-page-btn">Prev</Button>
        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="next-page-btn">Next</Button>
      </div>
    </div>
  );
}

export function SectionCard({ children, className, ...rest }) {
  return <div className={cn("bg-card border rounded-lg shadow-sm", className)} {...rest}>{children}</div>;
}

export function ExportButton({ filename, rows, columns, label = "Export CSV", testid = "export-csv-btn" }) {
  const onClick = () => {
    const ok = downloadCSV(filename, rows, columns);
    if (!ok) toast.error("No data to export");
    else toast.success(`Exported ${rows.length} row${rows.length === 1 ? "" : "s"}`);
  };
  return (
    <Button variant="outline" size="sm" onClick={onClick} data-testid={testid}>
      <Download className="h-3.5 w-3.5 mr-1.5" /> {label}
    </Button>
  );
}
